import { DOCUMENT } from '@angular/common';
import {
	computed,
	effect,
	inject,
	Injectable,
	Injector,
	signal,
} from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';

interface BeforeInstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{
		outcome: 'accepted' | 'dismissed';
		platform: string;
	}>;
}

@Injectable({
	providedIn: 'root',
})
export class PwaAccessService {
	private readonly injector = inject(Injector);
	private readonly document = inject(DOCUMENT);
	private readonly userService = inject(UserService);

	private readonly deferredPrompt = signal<BeforeInstallPromptEvent | null>(
		null
	);
	private readonly hasPwaAccessSignal = signal(false);
	private readonly registrationSubject = new ReplaySubject<void>(1);
	private readonly manifestSelector = `link[rel="manifest"][href="${environment.pwa.manifestPath}"]`;
	private readonly manifestManagedAttribute = 'data-cm-pwa-managed';
	private readonly cleanupReloadKey = 'cm-pwa-unauthorized-cleanup-reload';

	private initialized = false;
	private registrationTriggered = false;
	private lastAccess: boolean | null = null;

	readonly hasPwaAccess = computed(() => this.hasPwaAccessSignal());
	readonly canInstall = computed(
		() => this.hasPwaAccess() && this.deferredPrompt() !== null
	);
	readonly serviceWorkerRegistrationTrigger$ =
		this.registrationSubject.asObservable();

	private readonly beforeInstallPromptHandler = (event: Event): void => {
		const installEvent = event as BeforeInstallPromptEvent;
		installEvent.preventDefault();
		this.deferredPrompt.set(installEvent);
	};

	private readonly appInstalledHandler = (): void => {
		this.deferredPrompt.set(null);
	};

	initialize(): void {
		if (this.initialized) {
			return;
		}

		this.initialized = true;
		this.attachInstallEventListeners();

		effect(
			() => {
				if (!environment.pwa.enabled) {
					this.hasPwaAccessSignal.set(false);
					this.deferredPrompt.set(null);
					this.removeManifestLink();
					return;
				}

				const user = this.userService.userSig$();

				if (!user) {
					this.hasPwaAccessSignal.set(false);
					if (this.lastAccess !== null) {
						this.deferredPrompt.set(null);
					}
					this.lastAccess = null;
					this.removeManifestLink();
					return;
				}

				const hasPermission = this.userService.hasPermission(
					environment.pwa.requiredPermission
				);
				this.hasPwaAccessSignal.set(hasPermission);

				if (this.lastAccess === hasPermission) {
					return;
				}

				this.lastAccess = hasPermission;

				if (hasPermission) {
					this.ensureManifestLink();
					sessionStorage.removeItem(this.cleanupReloadKey);

					if (!this.registrationTriggered) {
						this.registrationTriggered = true;
						this.registrationSubject.next();
					}

					return;
				}

				this.deferredPrompt.set(null);
				this.removeManifestLink();
				void this.cleanupUnauthorizedPwaState().catch((error: unknown) => {
					console.error('Failed to clean up unauthorized PWA state', error);
				});
			},
			{ injector: this.injector }
		);
	}

	async promptInstall(): Promise<void> {
		const installPromptEvent = this.deferredPrompt();
		if (!installPromptEvent || !this.hasPwaAccess()) {
			return;
		}

		await installPromptEvent.prompt();
		await installPromptEvent.userChoice.catch(() => null);
		this.deferredPrompt.set(null);
	}

	private attachInstallEventListeners(): void {
		if (typeof window === 'undefined') {
			return;
		}

		window.addEventListener(
			'beforeinstallprompt',
			this.beforeInstallPromptHandler as EventListener
		);
		window.addEventListener('appinstalled', this.appInstalledHandler);
	}

	private ensureManifestLink(): void {
		const head = this.document.head;
		if (!head) {
			return;
		}

		let manifestLink = head.querySelector<HTMLLinkElement>(
			this.manifestSelector
		);

		if (!manifestLink) {
			manifestLink = this.document.createElement('link');
			manifestLink.setAttribute('rel', 'manifest');
			manifestLink.setAttribute('href', environment.pwa.manifestPath);
			head.appendChild(manifestLink);
		}

		manifestLink.setAttribute(this.manifestManagedAttribute, 'true');
	}

	private removeManifestLink(): void {
		const head = this.document.head;
		if (!head) {
			return;
		}

		const manifestLinks = Array.from(
			head.querySelectorAll<HTMLLinkElement>(this.manifestSelector)
		);

		manifestLinks.forEach((manifestLink) => {
			if (
				manifestLink.getAttribute(this.manifestManagedAttribute) ===
				'true'
			) {
				manifestLink.remove();
			}
		});
	}

	private async cleanupUnauthorizedPwaState(): Promise<void> {
		const hadServiceWorker = await this.unregisterAngularServiceWorkers();
		const hadServiceWorkerCaches =
			await this.removeAngularServiceWorkerCaches();
		const shouldReload = hadServiceWorker || hadServiceWorkerCaches;

		if (!shouldReload) {
			sessionStorage.removeItem(this.cleanupReloadKey);
			return;
		}

		if (sessionStorage.getItem(this.cleanupReloadKey) === '1') {
			return;
		}

		sessionStorage.setItem(this.cleanupReloadKey, '1');
		this.reloadPage();
	}

	private async unregisterAngularServiceWorkers(): Promise<boolean> {
		if (
			typeof navigator === 'undefined' ||
			!('serviceWorker' in navigator)
		) {
			return false;
		}

		const registrations = await navigator.serviceWorker.getRegistrations();
		if (!registrations.length) {
			return false;
		}

		let unregistered = false;

		for (const registration of registrations) {
			const workerScriptUrls = [
				registration.active?.scriptURL,
				registration.waiting?.scriptURL,
				registration.installing?.scriptURL,
			].filter((scriptUrl): scriptUrl is string => Boolean(scriptUrl));

			const ownsAngularServiceWorker = workerScriptUrls.some((scriptUrl) =>
				new URL(scriptUrl).pathname.endsWith(`/${environment.pwa.swScript}`)
			);

			if (!ownsAngularServiceWorker) {
				continue;
			}

			const wasUnregistered = await registration.unregister();
			unregistered = wasUnregistered || unregistered;
		}

		return unregistered;
	}

	private async removeAngularServiceWorkerCaches(): Promise<boolean> {
		if (typeof caches === 'undefined') {
			return false;
		}

		const cacheKeys = await caches.keys();
		const angularServiceWorkerCaches = cacheKeys.filter((cacheKey) =>
			cacheKey.startsWith('ngsw:')
		);

		if (!angularServiceWorkerCaches.length) {
			return false;
		}

		await Promise.all(
			angularServiceWorkerCaches.map((cacheKey) =>
				caches.delete(cacheKey)
			)
		);

		return true;
	}

	private reloadPage(): void {
		window.location.reload();
	}
}
